import * as apiClient from '../api/apiClient';
import { broadcastAuthLogout } from './authTabSync';
import {
  acquireRefreshLock,
  releaseRefreshLock,
  waitForRefreshLockRelease
} from './refreshQueue';
import * as tokenStorage from './tokenStorage';
import {
  getProfileIdsFromToken,
  getUsernameFromToken,
  getModulesFromToken,
  parseJwtPayload
} from './jwtClaims';
import { clearLegacyCompatSession, readLegacyCompatSession, setLegacyCompatSession } from '../legacy/legacySession';
import { AuthEventNames, logAuthEvent } from '../observability/authLogger';
import { createCorrelationId } from '../api/correlationId';
import {
  clearPersistedSession,
  clearTabSessionItemsOnly,
  getSessionModules,
  hydrateTabSessionFromSharedStorage,
  MODULES_KEY,
  setSessionItem
} from '../session/sessionStore';

let currentUser = null;
let currentClaims = null;
let bootstrapInFlight = null;

function syncUserFromToken(accessToken) {
  currentClaims = parseJwtPayload(accessToken);
  currentUser = {
    username: getUsernameFromToken(accessToken),
    profileIds: getProfileIdsFromToken(accessToken)
  };
  return currentUser;
}

function normalizeModuleNames(modules) {
  if (!Array.isArray(modules)) {
    return [];
  }

  const seen = new Set();
  const normalized = [];

  modules.forEach((module) => {
    const value = String(module).trim();
    if (!value) {
      return;
    }
    const key = value.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    normalized.push(value);
  });

  return normalized;
}

function extractModulesFromLoginPayload(data) {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const modules = data.modulos ?? data.Modulos ?? [];
  return Array.isArray(modules) ? modules : [];
}

function extractAccessTokenFromLoginPayload(data) {
  return data?.accessToken ?? data?.AccessToken ?? null;
}

function extractExpiresInFromLoginPayload(data) {
  return data?.expiresIn ?? data?.ExpiresIn ?? null;
}

function extractRefreshTokenFromLoginPayload(data) {
  return data?.refreshToken ?? data?.RefreshToken ?? null;
}

function normalizeLoginPayload(data) {
  const accessToken = extractAccessTokenFromLoginPayload(data);
  return {
    accessToken,
    refreshToken: extractRefreshTokenFromLoginPayload(data),
    expiresIn: extractExpiresInFromLoginPayload(data),
    modulos: extractModulesFromLoginPayload(data)
  };
}

function persistGrantedModules(dataOrModules, accessToken) {
  const fromResponse = Array.isArray(dataOrModules)
    ? dataOrModules
    : extractModulesFromLoginPayload(dataOrModules);
  const granted = normalizeModuleNames([
    ...fromResponse,
    ...getModulesFromToken(accessToken)
  ]);

  if (granted.length > 0) {
    setSessionItem(MODULES_KEY, JSON.stringify(granted));
  }

  return granted;
}

function ensureSessionModules(accessToken) {
  const stored = getSessionModules();
  if (stored.length > 0) {
    return stored;
  }

  const fromToken = normalizeModuleNames(getModulesFromToken(accessToken));
  if (fromToken.length > 0) {
    setSessionItem(MODULES_KEY, JSON.stringify(fromToken));
  }

  return fromToken;
}

function wireApiClient() {
  apiClient.configureAuthHandlers({
    onRefresh: (failedAccessToken) => refresh(failedAccessToken),
    onLogout: () => logout({ reason: 'refresh_failed' })
  });
}

wireApiClient();

function notifyAuthStateChanged() {
  window.dispatchEvent(new CustomEvent('authenticated'));
}

function hydrateSharedSessionStores() {
  tokenStorage.hydrateTabFromSharedStorage();
  hydrateTabSessionFromSharedStorage();
}

function hasViableLocalSession() {
  const restored = tokenStorage.restore();
  return Boolean(restored?.accessToken && !tokenStorage.isExpired());
}

function clearLocalTabSession() {
  tokenStorage.clearTabTokensOnly();
  clearTabSessionItemsOnly();
  currentUser = null;
  currentClaims = null;
}

export async function login(username, password) {
  const correlationId = createCorrelationId();
  const startedAt = Date.now();
  logAuthEvent(AuthEventNames.LOGIN_STARTED, {
    correlationId,
    user: username,
    endpoint: '/api/auth/login'
  });

  try {
    const { data } = await apiClient.post(
      '/api/auth/login',
      { username, password },
      { skipAuth: true, headers: { 'X-Correlation-Id': correlationId } }
    );

    const tokens = normalizeLoginPayload(data);
    if (!tokens.accessToken) {
      throw { status: 500, message: 'La respuesta de login no incluyó accessToken.' };
    }

    tokenStorage.setTokens(tokens);
    const user = syncUserFromToken(tokens.accessToken);
    const grantedModules = persistGrantedModules(tokens, tokens.accessToken);
    if (grantedModules.length === 0) {
      clearSession();
      throw {
        status: 403,
        message: 'El usuario no tiene módulos asignados. Contacte al administrador.'
      };
    }

    setLegacyCompatSession(username, user.profileIds);
    notifyAuthStateChanged();

    logAuthEvent(AuthEventNames.LOGIN_SUCCESS, {
      correlationId,
      user: username,
      endpoint: '/api/auth/login',
      durationMs: Date.now() - startedAt
    });

    return { user, tokens: data };
  } catch (error) {
    logAuthEvent(AuthEventNames.LOGIN_FAILED, {
      correlationId,
      user: username,
      endpoint: '/api/auth/login',
      durationMs: Date.now() - startedAt,
      status: error.status
    });
    throw error;
  }
}

export async function refresh(failedAccessToken) {
  const restoreIfSharedRenewal = () => {
    hydrateSharedSessionStores();
    const currentAccess = tokenStorage.getAccessToken();
    if (
      failedAccessToken
      && currentAccess
      && currentAccess !== failedAccessToken
      && hasViableLocalSession()
    ) {
      return restoreSession();
    }
    return null;
  };

  const restoreIfViable = () => {
    hydrateSharedSessionStores();
    if (hasViableLocalSession()) {
      return restoreSession();
    }
    return null;
  };

  const alreadyRenewed = restoreIfSharedRenewal();
  if (alreadyRenewed) {
    return alreadyRenewed;
  }

  if (!failedAccessToken) {
    const alreadyFresh = restoreIfViable();
    if (alreadyFresh) {
      return alreadyFresh;
    }
  }

  let lockOwner = await acquireRefreshLock();
  if (!lockOwner) {
    await waitForRefreshLockRelease(() => Boolean(restoreIfSharedRenewal() || (!failedAccessToken && restoreIfViable())));
    const recovered = restoreIfSharedRenewal() || (!failedAccessToken ? restoreIfViable() : null);
    if (recovered) {
      return recovered;
    }
    lockOwner = await acquireRefreshLock();
  }

  if (!lockOwner) {
    const lastChance = restoreIfSharedRenewal() || (!failedAccessToken ? restoreIfViable() : null);
    if (lastChance) {
      return lastChance;
    }
    logAuthEvent(AuthEventNames.TOKEN_EXPIRED, { endpoint: '/api/auth/refresh' });
    throw new Error('No refresh token');
  }

  try {
    const restored = restoreIfSharedRenewal();
    if (restored) {
      return restored;
    }

    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
      logAuthEvent(AuthEventNames.TOKEN_EXPIRED, { endpoint: '/api/auth/refresh' });
      throw new Error('No refresh token');
    }

    const correlationId = createCorrelationId();
    const startedAt = Date.now();

    const { data } = await apiClient.post(
      '/api/auth/refresh',
      { refreshToken },
      { skipAuth: true, headers: { 'X-Correlation-Id': correlationId } }
    );

    const tokens = normalizeLoginPayload(data);
    tokenStorage.setTokens(tokens);
    syncUserFromToken(tokens.accessToken);
    persistGrantedModules(tokens, tokens.accessToken);

    logAuthEvent(AuthEventNames.TOKEN_REFRESH, {
      correlationId,
      user: currentUser?.username,
      endpoint: '/api/auth/refresh',
      durationMs: Date.now() - startedAt
    });

    return data;
  } catch (error) {
    const recovered = restoreIfSharedRenewal();
    if (recovered) {
      return recovered;
    }
    throw error;
  } finally {
    releaseRefreshLock(lockOwner);
  }
}

export function prepareTabSession() {
  hydrateSharedSessionStores();
  return restoreSession();
}

export function restoreSession() {
  const restored = tokenStorage.restore();
  if (restored?.accessToken && !tokenStorage.isExpired()) {
    const user = syncUserFromToken(restored.accessToken);
    ensureSessionModules(restored.accessToken);
    const legacy = readLegacyCompatSession();
    if (!legacy.isLegacyAuthenticated && user.username) {
      setLegacyCompatSession(user.username, user.profileIds);
    }
    logAuthEvent(AuthEventNames.SESSION_RESTORED, { user: user.username });
    return { user, legacy: readLegacyCompatSession() };
  }

  return null;
}

export function needsBootstrapRefresh() {
  const restored = tokenStorage.restore();
  if (restored?.accessToken && !tokenStorage.isExpired()) {
    return false;
  }
  return Boolean(tokenStorage.getRefreshToken());
}

async function bootstrapSessionInternal() {
  hydrateSharedSessionStores();

  if (hasViableLocalSession()) {
    const session = restoreSession();
    notifyAuthStateChanged();
    return session;
  }

  if (tokenStorage.getRefreshToken()) {
    try {
      await refresh();
      const session = restoreSession();
      notifyAuthStateChanged();
      return session;
    } catch {
      clearSession({ skipBroadcast: true, scope: 'local' });
      return null;
    }
  }

  if (tokenStorage.getAccessToken()) {
    clearSession({ skipBroadcast: true, scope: 'local' });
  }

  notifyAuthStateChanged();
  return null;
}

export function bootstrapSession() {
  if (!bootstrapInFlight) {
    bootstrapInFlight = bootstrapSessionInternal().finally(() => {
      bootstrapInFlight = null;
    });
  }
  return bootstrapInFlight;
}

export function syncSessionFromSharedStorage() {
  hydrateSharedSessionStores();
  const session = restoreSession();
  notifyAuthStateChanged();
  return session;
}

export function isAuthenticated() {
  const token = tokenStorage.getAccessToken();
  if (!token || tokenStorage.isExpired()) {
    return false;
  }
  return ensureSessionModules(token).length > 0;
}

export function getUser() {
  if (currentUser) {
    return currentUser;
  }

  const token = tokenStorage.getAccessToken();
  if (token && !tokenStorage.isExpired()) {
    return syncUserFromToken(token);
  }

  return null;
}

export function getClaims() {
  return currentClaims;
}

export function removeTokens() {
  tokenStorage.clearTokens();
  currentUser = null;
  currentClaims = null;
}

export function clearSession(options = {}) {
  const { skipBroadcast = false, scope = 'global' } = options;

  if (scope === 'local') {
    clearLocalTabSession();
    notifyAuthStateChanged();
    return;
  }

  removeTokens();
  clearPersistedSession();
  clearLegacyCompatSession();
  currentUser = null;
  currentClaims = null;

  if (!skipBroadcast) {
    broadcastAuthLogout();
  }
  window.dispatchEvent(new CustomEvent('authenticated'));
  window.dispatchEvent(new CustomEvent('logout'));
}

export async function logout(options = {}) {
  clearSession();
  logAuthEvent(AuthEventNames.LOGOUT, {
    user: options.user,
    reason: options.reason
  });
  if (typeof window !== 'undefined' && window.location.pathname !== '/') {
    window.location.assign('/');
  }
}

export function resetAuthStateForTests() {
  bootstrapInFlight = null;
  clearSession({ skipBroadcast: true });
}
